import { TransactionType } from "~/endpoints/transactions/types";

// eslint-disable-next-line no-shadow
export enum PageTypes {
  INCOMING_INBOX = "incoming_inbox",
  INCOMING_WAITING = "incoming_waiting",
  INCOMING_COMPLETED = "incoming_completed",
  OUTGOING_OUTBOX = "outgoing_outbox",
  OUTGOING_WAITING = "outgoing_waiting",
  OUTGOING_PENDING = "outgoing_pending",
  OUTGOING_COMPLETED = "outgoing_completed",
  ALL_TRANSFERS = "all_transfers",
}

// eslint-disable-next-line no-shadow
export enum PageTypeStatusMap {
  ALL_TRANSFERS = "ALL_TRANSFERS",
  INCOMING_INBOX = "SENT,ACK",
  INCOMING_WAITING = "INCOMPLETE,MISSING_DATA_REQUESTED_LT_5_DAYS,MISSING_DATA_REQUESTED_GT_5_DAYS,ORIGINATOR_UNREACHABLE",
  INCOMING_COMPLETED = "ACCEPTED,DECLINED,REJECTED,NOT_READY,SAVED,CANCELLED",
  OUTGOING_OUTBOX = "NEW",
  OUTGOING_WAITING = "WAITING_FOR_INFORMATION,MISSING_BENEFICIARY_DATA,INCOMPLETE,MISSING_DATA_REQUESTED_LT_5_DAYS,MISSING_DATA_REQUESTED_GT_5_DAYS",
  OUTGOING_PENDING = "SENT,ACK",
  OUTGOING_COMPLETED = "ACCEPTED,DECLINED,REJECTED,NOT_READY,SAVED,CANCELLED",
}

// eslint-disable-next-line no-shadow
export enum PageTypeTitleMap {
  INCOMING_INBOX = "Inbox - Incoming Transfers",
  INCOMING_WAITING = "Waiting for data - Incoming Transfers",
  INCOMING_COMPLETED = "Completed - Incoming Transfers",
  OUTGOING_OUTBOX = "Outbox - Outgoing Transfers",
  OUTGOING_WAITING = "Waiting for data - Outgoing Transfers",
  OUTGOING_PENDING = "Pending response - Outgoing Transfers",
  OUTGOING_COMPLETED = "Completed - Outgoing Transfers",
  ALL_TRANSFERS = "All Transfers - Incoming & Outgoing",
}

export const TRANSACTION_TYPES: Record<TransactionType, string> = {
  [TransactionType.BELOW_THRESHOLD]: "Below Threshold",
  [TransactionType.NON_CUSTODIAL]: "Unhosted",
  [TransactionType.TRAVELRULE]: "Travel Rule",
  [TransactionType.UNKNOWN]: "Unknown",
};

export const REJECT_TRANSACTION_OPTIONS = [
  {
    value: "The blockchain address doesn’t belong to my company",
  },
  {
    value: "I don’t know if this blockchain address belongs to my company",
  },
  {
    value: "I don’t want to receive this Travel Rule transfer",
  },
];

//We want to show a different copy to the beneficiary in the details
export const REJECT_TRANSACTION_OPTIONS_BENEFICIARY_MAPPING: Record<string, string> = {
  "The blockchain address doesn’t belong to my company": "Destination address does not belong to beneficiary",
  "I don’t know if this blockchain address belongs to my company":
    "Beneficiary does not know if blockchain address belongs to company",
  "I don’t want to receive this Travel Rule transfer":
    "Beneficiary did not accept this Travel Rule transfer and did not specify the reason",
};

export const NOT_READY_TRANSACTION_OPTIONS = [
  {
    value: "Currently in the process of implementing the Travel Rule",
  },
  {
    value: "Not able to verify the receiving blockchain address",
  },
  {
    value: "Travel Rule requirements are not yet in force in my jurisdiction",
  },
  {
    value: "Other",
  },
];

//We want to show a different copy to the beneficiary
export const NOT_READY_TRANSACTION_OPTIONS_BENEFICIARY_MAPPING: Record<string, string> = {
  "Currently in the process of implementing the Travel Rule":
    "Currently in the process of implementing the Travel Rule",
  "Not able to verify the receiving blockchain address": "Not able to verify the receiving blockchain address",
  "Travel Rule requirements are not yet in force in my jurisdiction":
    "Travel Rule requirements are not yet in force in beneficiary's jurisdiction",
};

export const DECLINE_TRANSACTION_OPTIONS = [
  {
    value: "Counterparty VASP due diligence (incomplete / unsatisfactory)",
  },
  {
    value: "Originator blockchain address risk score is outside of our risk appetite",
  },
  {
    value: "Originator sanction screening result is outside of our risk appetite",
  },
  {
    value: "Originator data missing",
  },
  {
    value: "Beneficiary name does not match",
  },
  {
    value: "Beneficiary data missing",
  },
  {
    value: "Asset type not accepted",
  },
  {
    value: "Potential suspicious transaction issue",
  },
  {
    value: "Other",
  },
];

// eslint-disable-next-line no-shadow
export enum TransactionActionType {
  CREATE = "create",
  REDIRECT = "redirect",
  APPROVE = "approve",
  CANCEL = "cancel",
  UPDATE = "update",
  CONFIRM = "confirm",
  REJECT = "reject",
  NOTIFY = "notify",
  ACCEPT = "accept",
  DECLINE = "decline",
  RECEIVE = "receive",
  NOT_READY = "not_ready",
  SEND_TO_INBOX = "send_to_inbox",
}
export interface TransactionAction {
  hash: string;
  issuerDID: string;
  action: string;
  actionData: string | null;
  createdAt: any;
  createdBy: string;
  beneficiaryVASPFrom?: string;
  beneficiaryVASPTo?: string;
}

export type ActionSource =
  | "unknown" // a bucket for all the other types
  | "bulk"
  | "rules"
  | "auto"
  | "tfDelete"
  | "legacy"
  | "integrations"
  | "addressUpload"
  | "api"
  | "registerCustomer"
  | "sdk"
  | "dashboard"
  | "widget"
  | "protocol-gateways";

export type RuleCheck =
  | "ALL"
  | "JURISDICTION"
  | "RISK_SCORE"
  | "SANCTION_LIST_CHECK"
  | "TRANSACTION_AMOUNT"
  | "VASP"
  | "REVIEWED_VASP_LIST";

export type RulesActionData = {
  ruleId: string;
  ruleType: RuleCheck;
  ruleValue: string;
};

export type AddressConfirmationActionData = {
  callSource: "auto";
  confirmedBy: "address-book" | "manual" | "address-hash";
  isInBulk: boolean;
};

export type AddressRejectionActionData = {
  callSource: "auto";
  rejectedBy: "address-book" | "manual" | "address-hash";
  isInBulk: boolean;
};

type AnyActionData = {
  [key: string]: any;
};

export type ActionData = {
  callSource: ActionSource;
  reason?: string;
  updatedBy?: string;
  updatedFields?: string[];
  [key: string]: any;
} & (RulesActionData | AddressConfirmationActionData | AddressRejectionActionData | AnyActionData);

export type TransactionActionParsed = Omit<TransactionAction, "actionData"> & {
  actionData: ActionData | null;
};
